import { COUNTRY_MOBILE_CODES } from '@/lib/constants';
import { Validators } from '@/lib/validator';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Flag,
  FlagCountryCode,
  FormError,
  FormField,
  FormHint,
  FormLabel,
  FormLabelGroup,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@opaz/ui-components';
import examples from 'libphonenumber-js/mobile/examples';
import { CountryCode, getExampleNumber } from 'libphonenumber-js';
import { useMemo } from 'react';
import { SubmitHandler, useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { numericToAlpha2 } from 'i18n-iso-countries';
import { CountryResponse } from '@/api/backend/types';

interface PersonalDetailsInput {
  fullNameEn: string;
  fullNameAr: string;
  mobileCountryCode: FlagCountryCode;
  countryCodeNumeric: string;
  mobileNumber: string;
}

const phoneWithCountryValidator = Validators.validatePhoneWithCountry('mobileCountryCode', 'mobileNumber');

const personalDetailsFormSchema = z
  .object({
    fullNameEn: z
      .string()
      .min(1)
      .refine(...Validators.keycloakName),
    fullNameAr: z.string().refine(...Validators.keycloakName),
    mobileCountryCode: z.string() as z.ZodType<FlagCountryCode>,
    countryCodeNumeric: z.string().min(1),
    mobileNumber: z
      .string()
      .min(1)
      .refine(...Validators.stringIsNumber),
  })
  .refine(phoneWithCountryValidator.validator, {
    params: phoneWithCountryValidator.params,
    path: phoneWithCountryValidator.path,
  });

type PersonalDetailsFormProps = {
  data: PersonalDetailsInput;
  onComplete: (data: PersonalDetailsInput) => void;
  countries: CountryResponse[];
  isCountriesLoading: boolean;
};

export const PersonalDetailsForm = ({
  data: formData,
  onComplete,
  countries,
  isCountriesLoading,
}: PersonalDetailsFormProps) => {
  const { t } = useTranslation();

  const {
    control,
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<PersonalDetailsInput>({
    defaultValues: { ...formData, mobileCountryCode: formData.mobileCountryCode },
    resolver: zodResolver(personalDetailsFormSchema),
  });

  const { field: mobileCountryCodeField } = useController({
    control,
    name: 'mobileCountryCode',
  });

  const { field: countryCodeNumericField } = useController({
    control,
    name: 'countryCodeNumeric',
  });

  const onSubmit: SubmitHandler<PersonalDetailsInput> = async (data) => {
    onComplete(data);
  };

  const flagOptions = useMemo(() => {
    return COUNTRY_MOBILE_CODES.map((country) => {
      return (
        <SelectItem value={country.countryIso2} key={country.countryIso2}>
          <div className="flex items-center gap-2">
            <Flag countryCode={country.countryIso2} />
            {country.mobileCode}
          </div>
        </SelectItem>
      );
    });
  }, []);

  const getNationalityFlag = (countryCode: string) => {
    const alpha2 = numericToAlpha2(countryCode);

    if (!alpha2) {
      return null;
    }

    return <Flag countryCode={alpha2 as FlagCountryCode} />;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-10">
        <h1 className="font-bold text-3xl text-[#1A444F] mb-6">{t('register.title')}</h1>
        <p className="text-xl text-muted-foreground">{t('register.description')}</p>
      </div>

      <div className="flex flex-col gap-5">
        <FormField>
          <FormLabelGroup>
            <FormLabel error={!!errors.fullNameEn}>{t('register.fields.fullName')} *</FormLabel>
            {errors.fullNameEn && (
              <FormError>{t(errors.fullNameEn.message as unknown as TemplateStringsArray)}</FormError>
            )}
          </FormLabelGroup>

          <Input error={!!errors.fullNameEn} {...register('fullNameEn')} autoComplete="off"></Input>
        </FormField>

        <FormField>
          <FormLabelGroup>
            <FormLabel error={!!errors.fullNameAr}>
              {t('register.fields.fullNameAr')} <span className="font-normal">{`(${t('common.optional')})`}</span>
            </FormLabel>

            {errors.fullNameAr && (
              <FormError>{t(errors.fullNameAr.message as unknown as TemplateStringsArray)}</FormError>
            )}
          </FormLabelGroup>

          <Input error={!!errors.fullNameAr} {...register('fullNameAr')} autoComplete="off"></Input>
        </FormField>

        <FormField>
          <FormLabelGroup>
            <FormLabel error={!!errors.countryCodeNumeric}>{t('register.fields.nationality')} *</FormLabel>
            {errors.countryCodeNumeric && (
              <FormError>{t(errors.countryCodeNumeric.message as unknown as TemplateStringsArray)}</FormError>
            )}
          </FormLabelGroup>

          <Select
            value={countryCodeNumericField.value}
            onValueChange={countryCodeNumericField.onChange}
            disabled={isCountriesLoading}
            error={!!errors.countryCodeNumeric}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={isCountriesLoading ? t('common.loading') : t('register.fields.nationalityPlaceholder')}
              />
            </SelectTrigger>
            <SelectContent>
              {countries.map((country) => (
                <SelectItem value={country.code} key={country.code}>
                  <div className="flex items-center gap-2">
                    {getNationalityFlag(country.code)}
                    <span className="text-sm">{country.nameEn}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        <FormField className="mb-10">
          <FormLabelGroup>
            <FormLabel error={!!errors.mobileNumber}>{t('register.fields.mobileNumber')} *</FormLabel>

            {errors.mobileNumber && (
              <FormError>{t(errors.mobileNumber.message as unknown as TemplateStringsArray)}</FormError>
            )}
          </FormLabelGroup>

          <div className="flex gap-3">
            <Select value={mobileCountryCodeField.value} onValueChange={mobileCountryCodeField.onChange}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>{flagOptions}</SelectContent>
            </Select>

            <div className="w-full">
              <Input className="w-full mb-3" error={!!errors.mobileNumber} {...register('mobileNumber')} />

              <FormHint>
                {t('common.mobileNumberHint', {
                  mobileNumber: getExampleNumber(mobileCountryCodeField.value as CountryCode, examples)?.nationalNumber,
                })}
              </FormHint>
            </div>
          </div>
        </FormField>
      </div>

      <Button className="w-full mb-8" type="submit">
        {t('register.actionButtonLabel')}
      </Button>
    </form>
  );
};
